import React, {createContext, useContext, useState} from 'react';
import {EEToolAdapterContextType} from "../types/EEToolAdapterContextType";
import {EEToolContextMenuItem} from "../types/EEToolContextMenuItem";
import {PopupLocation} from "../types/PopupLocation";
import {PopupSize} from "../types/PopupSize";
import {ColorPopupResult} from "../types/ColorPopupResult";
import {ProductImage} from "../types/ProductImage";
import {EdgeColorData} from "../tool/drawData";
import {IdDescriptionPair} from "../tool/includes/apiService";
import {showErrorMessage as utilsShowErrorMessage} from "../../../../utils/messages";

const EEToolAdapterContext = createContext<EEToolAdapterContextType>({} as EEToolAdapterContextType);
const useEEToolAdapterContext = () => useContext(EEToolAdapterContext);

function EEToolAdapterContextProvider(props: React.PropsWithChildren<unknown>) {

    const [drawApp, setDrawApp] = React.useState<any>(undefined);

    const [isPointTextVisible, setIsPointTextVisible] = useState(false);
    const [pointTextValue, setPointTextValue] = useState('');
    const [pointTextLeft, setPointTextLeft] = useState(0);
    const [pointTextTop, setPointTextTop] = useState(0);
    const [pointTextFn, setPointTextFn] = useState(() => (val: string) => {
    });

    const [isAngleVisible, setIsAngleVisible] = useState(false);
    const [angleValue, setAngleValue] = useState('');
    const [angleLeft, setAngleLeft] = useState(0);
    const [angleTop, setAngleTop] = useState(0);
    const [angleFn, setAngleFn] = useState(() => (val: string) => {
    });
    const [isEstimate, setIsEstimate] = useState(false);

    const [contextMenuItems, setContextMenuItems] = useState<EEToolContextMenuItem[]>([])
    const [contextMenuOnHidden, setContextMenuOnHidden] = useState(() => () => {
    });

    const [currentStep, setCurrentStep] = useState(1);

    const [popupColorVisible, setPopupColorVisible] = useState(false);
    const [popupSize, setPopupSize] = useState<PopupSize>({width: 0, height: 0});
    const [popupLocation, setPopupLocation] = useState<PopupLocation>({left: 0, top: 0});
    const [colorFn, setColorFn] = useState(() => (ec: ColorPopupResult) => {
    });
    const [edgeColorP, setEdgeColorP] = useState<number>(710);
    const [edgeColorE1, setEdgeColorE1] = useState<number>(0);
    const [edgeColorC, setEdgeColorC] = useState<number>(0);
    const [imageCacheFn, setImageCacheFn] = useState(() => (val: ProductImage) => {
    });

    const [popupFullRadiusVisible, setPopupFullRadiusVisible] = useState(false);
    const [radDepth, setRadDepth] = useState(10);
    const [radDirection, setRadDirection] = useState(1);
    const [fullRadiusFn, setFullRadiusFn] = useState(() => (radDepth: number, radDirection: number) => {
    });

    // Bump
    const [popupBumpVisible, setPopupBumpVisible] = useState(false);
    const [bumpWidth, setBumpWidth] = useState(10);
    const [bumpDepth, setBumpDepth] = useState(10);
    const [bumpFn, setBumpFn] = useState(() => (bumpWidth: number, bumpDepth: number) => {
    });

    // SelDepth
    const [popupSelDepthVisible, setPopupSelDepthVisible] = useState(false);
    const [popupSelDepthOptionalProductsVisible, setPopupSelDepthOptionalProductsVisible] = useState(false);
    const [selDepth, setSelDepth] = useState(10);
    const [selDepthOptionalProduct, setSelDepthOptionalProduct] = useState<number | undefined>(undefined);
    const [selDepthOptionalProducts, setSelDepthOptionalProducts] = useState<{ Id: number, Description: string }[]>([]);
    const [selDepthFn, setSelDepthFn] = useState(() => (selDepth: number, optionalProduct: number | undefined) => {
    });

    // CookTopSplash
    const [popupCookTopSplashVisible, setPopupCookTopSplashVisible] = useState(false);
    const [cookTopSplashWidth, setCookTopSplashWidth] = useState(0);
    const [cookTopSplashHeight, setCookTopSplashHeight] = useState(0);
    const [cookTopSplashFn, setCookTopSplashFn] = useState(() => (width: number, height: number) => {
    })

    // SplashHeight
    const [popupSplashHeightVisible, setPopupSplashHeightVisible] = useState(false);
    const [splashHeight, setSplashHeight] = useState(0);
    const [splashHeightFn, setSplashHeightFn] = useState(() => (height: number) => {
    })

    // Laminate
    const [popupLaminateVisible, setPopupLaminateVisible] = useState(false);
    const [popupLaminateTitle, setPopupLaminateTitle] = useState('');
    const [laminateProduct, setLaminateProduct] = useState<number | undefined>(undefined);
    const [laminateProducts, setLaminateProducts] = useState<{ Id: number, Description: string }[]>([]);
    const [laminateFn, setLaminateFn] = useState(() => (val: number) => {
    })

    function showPopupBump(location: PopupLocation, size: PopupSize) {
        setPopupSize(size);
        setPopupLocation(location);
        setPopupBumpVisible(true);
    }

    function hidePopupBump() {
        setPopupBumpVisible(false);
    }

    function showPopupSelDepth(location: PopupLocation, size: PopupSize) {
        setPopupSize(size);
        setPopupLocation(location);
        setPopupSelDepthOptionalProductsVisible(false);
        setPopupSelDepthVisible(true);
    }

    function showPopupSelDepthWithOptions(selectedProduct: number | undefined, options: {
        Id: number,
        Description: string
    }[], location: PopupLocation, size: PopupSize) {
        setPopupSize(size);
        setPopupLocation(location);
        setSelDepthOptionalProduct(selectedProduct);
        setSelDepthOptionalProducts(options);
        setPopupSelDepthOptionalProductsVisible(true);
        setPopupSelDepthVisible(true);
    }


    function hidePopupSelDepth() {
        setPopupSelDepthVisible(false);
    }

    function showPopupColor(location: PopupLocation, size: PopupSize) {
        setPopupSize(size);
        setPopupLocation(location);
        setPopupColorVisible(true);
    }

    function hidePopupColor() {
        setPopupColorVisible(false);
    }

    function showPopupFullRadius(location: PopupLocation, size: PopupSize) {
        setPopupSize(size);
        setPopupLocation(location);
        setPopupFullRadiusVisible(true);
    }

    function hidePopupFullRadius() {
        setPopupFullRadiusVisible(false);
    }

    function handleFullRadius(radDepth: number, radDirection: number) {
        hidePopupFullRadius();
        fullRadiusFn(radDepth, radDirection);
    }

    function setFullRadiusHandler(fn: (radDepth: number, radDirection: number) => void) {
        setFullRadiusFn(() => (radDepth: number, radDirection: number) => fn(radDepth, radDirection));
    }

    function handleBump(bumpWidth: number, bumpDepth: number) {
        hidePopupBump();
        bumpFn(bumpWidth, bumpDepth);
    }

    function setBumpHandler(fn: (bumpWidth: number, bumpDepth: number) => void) {
        setBumpFn(() => (bumpWidth: number, bumpDepth: number) => fn(bumpWidth, bumpDepth));
    }

    function showPopupSplashHeight(location: PopupLocation, size: PopupSize) {
        setPopupSize(size);
        setPopupLocation(location);
        setPopupSplashHeightVisible(true);
    }

    function handleSplashHeight(height: number) {
        hidePopupSplashHeight();
        splashHeightFn(height);
    }

    function setSplashHeightHandler(fn: (height: number) => void) {
        setSplashHeightFn(() => (height: number) => fn(height));
    }

    function hidePopupSplashHeight() {
        setPopupSplashHeightVisible(false);
    }

    function showPopupCookTopSplash(location: PopupLocation, size: PopupSize) {
        setPopupSize(size);
        setPopupLocation(location);
        setPopupCookTopSplashVisible(true);
    }

    function handleCookTopSplash(width: number, height: number) {
        hidePopupCookTopSplash();
        cookTopSplashFn(width, height);
    }

    function setCookTopSplashHandler(fn: (width: number, height: number) => void) {
        setCookTopSplashFn(() => (width: number, height: number) => fn(width, height));
    }

    function hidePopupCookTopSplash() {
        setPopupCookTopSplashVisible(false);
    }

    function showPopupLaminate(title: string, val: number | undefined, options: IdDescriptionPair[], location: PopupLocation, size: PopupSize) {
        setPopupSize(size);
        setPopupLocation(location);
        setPopupLaminateTitle(title);
        setLaminateProduct(val);
        setLaminateProducts(options);
        setPopupLaminateVisible(true);
    }

    function handleLaminate(value: number) {
        hidePopupLaminate();
        laminateFn(value);
    }

    function setLaminateHandler(fn: (value: number) => void) {
        setLaminateFn(() => (value: number) => fn(value));
    }

    function hidePopupLaminate() {
        setPopupLaminateVisible(false);
    }

    function handleSelDepth(selDepth: number, optionalProduct: number | undefined) {
        hidePopupSelDepth();
        selDepthFn(selDepth, optionalProduct);
    }

    function setSelDepthHandler(fn: (selDepth: number, optionalProduct: number | undefined) => void) {
        setSelDepthFn(() => (selDepth: number, optionalProduct: number | undefined) => fn(selDepth, optionalProduct));
    }

    function setColorHandler(fn: (ec: ColorPopupResult) => void) {
        setColorFn(() => (ec: ColorPopupResult) => fn(ec));
    }

    function handleColor(ec: ColorPopupResult) {
        hidePopupColor();
        colorFn(ec);
    }

    function showPointText(left: number, top: number, value: number, fn: (val: string) => void) {
        setIsPointTextVisible(true);
        setPointTextValue(value.toString());
        setPointTextLeft(left);
        setPointTextTop(top);
        setPointTextFn(() => (val: string) => fn(val));
    }

    function hidePointText() {
        setIsPointTextVisible(false);
    }

    function showAngle(left: number, top: number, value: number) {
        setIsAngleVisible(true);
        setAngleValue(value.toString());
        setAngleLeft(left);
        setAngleTop(top);
    }

    function hideAngle() {
        setIsAngleVisible(false);
    }

    function setAngleHandler(fn: (val: string) => void) {
        setAngleFn(() => (val: string) => fn(val));
    }

    function handleAngle(val: string) {
        angleFn(val);
    }

    function showContextMenu(items: EEToolContextMenuItem[], contextMenuOnHidden: () => void) {
        setContextMenuItems(items);
        setContextMenuOnHidden(() => () => {
            contextMenuOnHidden();
        });
    }

    function addImageToCache(img: ProductImage) {
        imageCacheFn(img)
    }

    function setAddImageToCacheHandler(fn: (val: ProductImage) => void) {
        setImageCacheFn(() => (val: ProductImage) => fn(val));
    }

    function showErrorMessage(message: string){
        utilsShowErrorMessage(message);
    }

    return (
        <EEToolAdapterContext.Provider value={
            {
                isPointTextVisible,
                pointTextValue,
                pointTextLeft,
                pointTextTop,
                setPointTextValue,
                pointTextFn,
                showPointText,
                hidePointText,
                showAngle,
                hideAngle,
                isAngleVisible,
                angleValue,
                angleLeft,
                angleTop,
                setAngleHandler,
                handleAngle,
                contextMenuItems,
                showContextMenu,
                contextMenuOnHidden,
                currentStep,
                setCurrentStep,
                popupColorVisible,
                popupFullRadiusVisible,
                showPopupColor,
                showPopupFullRadius,
                hidePopupColor,
                hidePopupFullRadius,
                popupSize,
                popupLocation,
                setColorHandler,
                handleColor,
                addImageToCache,
                setAddImageToCacheHandler,
                edgeColorP,
                setEdgeColorP,
                edgeColorE1,
                setEdgeColorE1,
                edgeColorC,
                setEdgeColorC,
                radDepth,
                setRadDepth,
                radDirection,
                setRadDirection,
                handleFullRadius,
                setFullRadiusHandler,
                popupBumpVisible,
                showPopupBump,
                hidePopupBump,
                bumpWidth,
                setBumpWidth,
                bumpDepth,
                setBumpDepth,
                handleBump,
                setBumpHandler,
                popupSelDepthVisible,
                showPopupSelDepth,
                showPopupSelDepthWithOptions,
                hidePopupSelDepth,
                selDepth,
                setSelDepth,
                selDepthOptionalProduct,
                setSelDepthOptionalProduct,
                popupSelDepthOptionalProductsVisible,
                handleSelDepth,
                setSelDepthHandler,
                selDepthOptionalProducts,
                popupCookTopSplashVisible,
                showPopupCookTopSplash,
                hidePopupCookTopSplash,
                cookTopSplashWidth,
                setCookTopSplashWidth,
                cookTopSplashHeight,
                setCookTopSplashHeight,
                handleCookTopSplash,
                setCookTopSplashHandler,
                popupSplashHeightVisible,
                showPopupSplashHeight,
                hidePopupSplashHeight,
                splashHeight,
                setSplashHeight,
                handleSplashHeight,
                setSplashHeightHandler,
                popupLaminateVisible,
                showPopupLaminate,
                hidePopupLaminate,
                laminateProduct,
                popupLaminateTitle,
                setLaminateProduct,
                handleLaminate,
                setLaminateHandler,
                laminateProducts,
                setLaminateProducts,
                drawApp,
                setDrawApp,
                isEstimate,
                setIsEstimate,
                showErrorMessage
            }
        }
                                       {...props}
        />);
}

export {EEToolAdapterContextProvider, useEEToolAdapterContext}
